import { AppMeta, LogContext, LoggerType, LogMessage } from '../types';

export const JS_LOG_EVENT_NAME = 'psc_react_log_event';

/**
 * Creates a jsLogger instance with its own configuration
 * @param app - The app metadata to use for all subsequent log events
 * @param initialContext - The initial context to use for all subsequent log events
 * @returns A jsLogger object with logging methods that use the config app metadata and initial context
 */
const createJsLogger = (app: AppMeta, initialContext?: LogContext): LoggerType => {
  // Each logger instance gets its own configuration
  const config = { app };

  const emitJsLogEvent = ({
    level,
    message,
    context = {},
    app: overrideApp
  }: Omit<LogMessage, 'timestamp'> & { app?: AppMeta }): void => {
    const event = new CustomEvent(JS_LOG_EVENT_NAME, {
      detail: {
        app: overrideApp || config.app,
        level,
        message,
        timestamp: Date.now(),
        context: { ...initialContext, ...context, jsEventLog: true }
      }
    });
    window.dispatchEvent(event);
  };

  return {
    info: (message: string, context?: LogContext) => {
      emitJsLogEvent({ level: 'info', message, context });
    },
    warn: (message: string, context?: LogContext) => {
      emitJsLogEvent({ level: 'warn', message, context });
    },
    error: (message: string, context?: LogContext) => {
      emitJsLogEvent({ level: 'error', message, context });
    },
    log: (message: string, context?: LogContext) => {
      emitJsLogEvent({ level: 'log', message, context });
    }
  };
};

/**
 * Initialize the JS Logger configuration
 * @param app - The app metadata to use for all subsequent log events
 * @returns A jsLogger object with logging methods that use the config app metadata
 */
export const initializeJsLogger = (app: AppMeta, initialContext?: LogContext): LoggerType => {
  return createJsLogger(app, initialContext);
};
